import axios from 'axios';
import { alert, AlertType } from '@quyva/shared-ui';
import { Config } from '../config/envConfig';
import { getUrl } from '../components/pdfDisplay/utils';

// Full URL for the refresh-token endpoint (matches the domain cookies are scoped to)
const REFRESH_TOKEN_URL = () =>
    getUrl(
        `http://${Config.NODE_SERVER_HOST}:${Config.NODE_SERVER_PORT}/refresh-token`
    );

// Mutex and queue for token refresh
let isRefreshing = false;
let failedQueue = [];
// A terminal auth failure hits every in-flight request at once (the page fires
// several calls in parallel), and each one used to run its own alert + redirect,
// so the user got a stack of identical popups. Handle the first, ignore the rest.
let sessionExpiredHandled = false;
// window.alert blocked until dismissed, so the redirect below waited for the
// user. The shared alert is a non-blocking toast, so navigating straight away
// would wipe it off screen before it could be read.
const SESSION_EXPIRED_REDIRECT_DELAY_MS = 2000;

// Proactive token refresh
let refreshTimer = null;
const TOKEN_REFRESH_INTERVAL = 25 * 60 * 1000; // 25 minutes (refresh before 30-min expiration)

/**
 * Process all queued requests after refresh completes
 */
const processQueue = (error) => {
    failedQueue.forEach((prom) => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve();
        }
    });

    failedQueue = [];
};

/**
 * Proactively refresh token before expiration
 */
const refreshTokenProactively = async () => {
    // Don't refresh if already refreshing
    if (isRefreshing) {
        return;
    }

    // Check if user is logged in
    const currentUser = localStorage.getItem('current');
    if (!currentUser) {
        stopProactiveRefresh();
        return;
    }

    try {
        isRefreshing = true;
        await axios.post(
            REFRESH_TOKEN_URL(),
            {},
            {
                withCredentials: true,
            }
        );
        console.log('🔄 Token refreshed proactively');
        isRefreshing = false;
    } catch (error) {
        console.error('❌ Proactive token refresh failed:', error);
        isRefreshing = false;

        // If refresh fails, stop the timer and let the user re-login
        stopProactiveRefresh();

        // Only end the session on a real auth rejection, not a network blip.
        if (error.response?.status === 401) {
            handleSessionExpired('Your session has expired. Please login again.');
        }
    }
};

/**
 * Start proactive token refresh timer
 */
const startProactiveRefresh = () => {
    // Clear any existing timer
    stopProactiveRefresh();

    // Start new timer
    refreshTimer = setInterval(refreshTokenProactively, TOKEN_REFRESH_INTERVAL);
    console.log('✅ Proactive token refresh started (every 25 minutes)');
};

/**
 * Stop proactive token refresh timer
 */
const stopProactiveRefresh = () => {
    if (refreshTimer) {
        clearInterval(refreshTimer);
        refreshTimer = null;
        console.log('🛑 Proactive token refresh stopped');
    }
};

/**
 * Terminal auth failure: clear the session, tell the user once, redirect once.
 * Safe to call from every failing request -- only the first call acts.
 */
const handleSessionExpired = (message) => {
    if (sessionExpiredHandled) {
        return;
    }
    sessionExpiredHandled = true;
    localStorage.removeItem('current');
    stopProactiveRefresh();
    if (
        window.location.pathname === '/login' ||
        window.location.pathname === '/'
    ) {
        return;
    }
    if (message) {
        alert(AlertType.ERROR, message);
        setTimeout(() => {
            window.location.href = '/login';
        }, SESSION_EXPIRED_REDIRECT_DELAY_MS);
        return;
    }
    window.location.href = '/login';
};

/**
 * Initialize proactive refresh if user is logged in
 */
if (localStorage.getItem('current')) {
    startProactiveRefresh();
}

/**
 * Response interceptor for handling 401 errors and token refresh
 */
axios.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        // Check if error is 401 and not already retried
        if (error.response?.status === 401 && !originalRequest._retry) {
            const code = error.response?.data?.code;

            // ── Security violations: redirect immediately, no refresh attempt ──
            if (code === 'TOKEN_REVOKED') {
                handleSessionExpired('Your session has expired. Please login again.');
                return Promise.reject(error);
            }

            if (code === 'TOKEN_REUSE') {
                console.error('🚨 Token reuse detected, all sessions revoked');
                handleSessionExpired(
                    'Security alert: Token reuse detected. Please login again.'
                );
                return Promise.reject(error);
            }

            // The token verified but the account behind it is gone. Nothing to
            // refresh, and leaving the tab logged in just fails every later
            // request silently.
            if (code === 'USER_NOT_FOUND') {
                handleSessionExpired(
                    'Your account is no longer available. Please login again.'
                );
                return Promise.reject(error);
            }

            // ── Refreshable codes: try refresh before giving up ──
            // TOKEN_EXPIRED  – JWT/DB expiry
            // NO_TOKEN       – access_token cookie gone (e.g. idle > 30 min, browser cleared it)
            // TOKEN_INVALIDATED – stale token in header after rotation; cookie will be fresh after refresh
            // INVALID_TOKEN  – bad signature (e.g. SECRET_KEY rotated after restart)
            const isRefreshableCode =
                code === 'TOKEN_EXPIRED' ||
                code === 'NO_TOKEN' ||
                code === 'TOKEN_INVALIDATED' ||
                code === 'INVALID_TOKEN';

            if (isRefreshableCode) {
                // Avoid infinite loop: don't try to refresh the refresh endpoint itself
                if (originalRequest.url?.includes('refresh-token')) {
                    return Promise.reject(error);
                }

                // No stored user means there is no session to refresh, and once a
                // terminal failure is handled further attempts only add noise.
                // Otherwise every request from a logged-out tab fires its own
                // refresh round-trip and its own 401.
                if (!localStorage.getItem('current') || sessionExpiredHandled) {
                    return Promise.reject(error);
                }

                // If already refreshing, queue this request
                if (isRefreshing) {
                    return new Promise((resolve, reject) => {
                        failedQueue.push({ resolve, reject });
                    })
                        .then(() => {
                            // Clear stale Authorization header before retry
                            delete originalRequest.headers['authorization'];
                            return axios(originalRequest);
                        })
                        .catch((err) => Promise.reject(err));
                }

                // Mark as retrying to prevent infinite loop
                originalRequest._retry = true;
                isRefreshing = true;

                try {
                    await axios.post(
                        REFRESH_TOKEN_URL(),
                        {},
                        { withCredentials: true }
                    );

                    // Refresh succeeded – process queue and restart proactive timer
                    processQueue(null);
                    isRefreshing = false;
                    startProactiveRefresh();

                    // Clear stale Authorization header and retry original request
                    delete originalRequest.headers['authorization'];
                    return axios(originalRequest);
                } catch (refreshError) {
                    // Capture before clearing — unauthenticated users on public pages
                    // should not be force-redirected to /login on failed API calls.
                    const wasAuthenticated = !!localStorage.getItem('current');
                    processQueue(refreshError);
                    isRefreshing = false;

                    // TOKEN_REVOKED / TOKEN_REUSE from the refresh endpoint are already
                    // handled with an alert by this same interceptor (nested call above).
                    // For all other refresh failures, show a session-expired message.
                    const refreshCode = refreshError.response?.data?.code;
                    const alreadyHandled =
                        refreshCode === 'TOKEN_REVOKED' ||
                        refreshCode === 'TOKEN_REUSE';

                    if (!refreshError.response) {
                        // Network error, not a rejection: the session may still be
                        // valid, so keep it and let the caller handle the failure.
                        console.error(
                            '❌ Token refresh failed due to network error'
                        );
                    } else if (!alreadyHandled && wasAuthenticated) {
                        handleSessionExpired(
                            'Your session has expired. Please login again.'
                        );
                    } else {
                        // Already alerted by the nested call, or never authenticated.
                        localStorage.removeItem('current');
                        stopProactiveRefresh();
                    }

                    return Promise.reject(refreshError);
                }
            }
        }

        return Promise.reject(error);
    }
);

/**
 * Request interceptor to ensure credentials are sent only to Node.js server
 */
axios.interceptors.request.use(
    (config) => {
        // Only send credentials (cookies) to Node.js server
        // FastAPI server doesn't need credentials and uses wildcard CORS
        const url = config.url || '';
        const isNodeServer =
            url.includes(`:${Config.NODE_SERVER_PORT}`) ||
            (!url.includes(`:${Config.FAST_API_PORT}`) &&
                !url.includes('http'));

        if (isNodeServer) {
            config.withCredentials = true;
        }

        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

/**
 * When the tab becomes visible again, immediately refresh the token.
 * This handles browser timer throttling in background/inactive tabs,
 * which can prevent the 25-minute setInterval from firing on time.
 */
if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
        if (
            document.visibilityState === 'visible' &&
            localStorage.getItem('current')
        ) {
            refreshTokenProactively();
        }
    });
}

// Export utility functions for manual control if needed
export { startProactiveRefresh, stopProactiveRefresh };

export default axios;
